// AGC: [AR][MANEJO] Client for the Argentina manejo-generation endpoint.
// AGC: Calls POST /api/v1/recommendations/manejo (AR-only) and returns the typed plan. The caller
// AGC: (FieldTaskPlanning) maps it to its local PlannedTask shape — same pattern as the Brazil
// AGC: fetchTaskTemplatesFromBackend → generateTasksFromTemplate flow. Brazil path is never affected.
import { getBackendUrl } from '../utils/backendUrl'

// AGC: A suggested input for an AR task (denormalized from the AR catalog / template).
export interface ArManejoInput {
  inputId: string
  inputName: string
  category: string
  quantityPerHa: number
  unit: string
  totalQuantity: number | null // AGC: quantityPerHa × area when the lot area is known
}

// AGC: Per-task traceability — fuente/método/justificación/parámetros (cited, editable first feed).
export interface ArManejoMeta {
  fuente?: string
  metodo?: string
  justificacion?: string
  parametros?: Record<string, unknown> | null
}

// AGC: One generated task. category is already mapped to the 4 persisted categories the app stores.
export interface ArManejoTask {
  key: string
  title: string
  phase: string // AGC: siembra|fertilizacion|aplicacion|monitoreo|cosecha (AR semantics, for display)
  category: 'plantio' | 'pulverizacao' | 'adubacao' | 'colheita'
  icon: string
  stage: string // AGC: anchored ORA phenology stage (SIEMBRA, DESARROLLO, ...)
  dds: number
  daysFromPlanting: number
  date: string // AGC: YYYY-MM-DD (already anchored to the sowing window, clamped >= today)
  inputs: ArManejoInput[]
  metadata: ArManejoMeta | null
}

// AGC: data_status: 'ok' | 'no_phenology' | 'no_template' | 'outside_zone' | 'not_ar' | 'no_crop'
export interface ArManejoPlan {
  provider: string
  data_status: string
  zone: { id: string; name: string } | null
  crop: string | null
  planting_date: string | null
  cycleDays: number | null
  tasks: ArManejoTask[]
  sources: string[]
  generated_at: string
}

/**
 * AGC: Fetch the AR manejo plan for an adopted crop on a lot. Returns null on transport/parse failure
 * AGC: so the caller can fall back to the existing manual soft gate.
 */
export async function fetchArManejoPlan(params: {
  cropType: string
  coordinates: [number, number][]
  areaHa?: number | null
  plantingMonth?: number | null
  token?: string
}): Promise<ArManejoPlan | null> {
  try {
    const backendUrl = getBackendUrl()
    const response = await fetch(`${backendUrl}/api/v1/recommendations/manejo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(params.token ? { Authorization: `Bearer ${params.token}` } : {}),
      },
      body: JSON.stringify({
        crop: params.cropType,
        coordinates: params.coordinates,
        areaHa: params.areaHa ?? null,
        plantingMonth: params.plantingMonth ?? null,
      }),
    })

    if (!response.ok) {
      console.warn(`⚠️ AGC: [AR][MANEJO] endpoint returned ${response.status}`)
      return null
    }

    const data = await response.json()
    if (!data?.success || !data?.data) return null
    return data.data as ArManejoPlan
  } catch (error) {
    console.warn('⚠️ AGC: [AR][MANEJO] fetch failed:', error)
    return null
  }
}
